/**
 * OBLIVION free gift tiers — ported from "DEV // Free Gift v2" (Horizon) to the Botanical / Halloween theme.
 *
 * - ONE free gift per order.
 * - Any paid product in the cart unlocks the Tier 1 list; paid subtotal ≥ ₹599 also unlocks the Tier 2 (premium) list.
 * - Picking a gift from either list replaces the current gift.
 * - After every cart change, removes a gift whose list the cart no longer unlocks, collapses duplicates
 *   and clamps gift quantity to 1.
 * - Before checkout, re-checks the cart so a gift can never reach checkout ineligible.
 *
 * Config comes from the server-rendered [data-ob-free-gift-config] JSON (snippets/oblivion-free-gift.liquid),
 * which reads Content > Metaobjects > Free gift settings. The ₹0 price comes from the
 * "FREE GIFT – Regular / Premium" automatic discounts.
 *
 * Theme hooks used (assets/oblivion.js + assets/botanical-features.js):
 *   window.OblivionUI.renderCart(cart)  re-renders the cart drawer and fires `botanical:cart`
 *   `botanical:cart` event              fired after every cart change in the drawer
 *   [data-cart-page]                    present on /cart, which is server-rendered, so it reloads
 *   [data-botanical-checkout]           the theme's checkout buttons (GoKwik)
 */
(() => {
  if (window.OBFreeGift) return;

  const routeRoot = (window.Oblivion?.root || window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');

  let cachedConfig = null;
  let reconcileTimer = null;
  let pendingWork = 0;
  let running = Promise.resolve();
  let announcing = false;

  /* ---------- config ---------- */

  function readConfig() {
    const nodes = document.querySelectorAll('[data-ob-free-gift-config]');
    const node = nodes[nodes.length - 1];
    if (node) {
      try {
        const parsed = JSON.parse(node.textContent);
        cachedConfig = {
          enabled: parsed.enabled !== false,
          t1: Number(parsed.t1) || 0,
          t2: Number(parsed.t2) || 0,
          tier1: (parsed.tier1 || []).map(String),
          tier2: (parsed.tier2 || []).map(String),
        };
      } catch (error) {
        console.warn('[ob-free-gift] invalid config', error);
      }
    }
    return cachedConfig;
  }

  /* ---------- cart helpers ---------- */

  async function getCart() {
    const response = await fetch(`${routeRoot}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!response.ok) throw new Error('Unable to read cart');
    return response.json();
  }

  async function post(path, payload) {
    const response = await fetch(`${routeRoot}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status >= 400) {
      throw new Error(data.description || data.message || 'Cart update failed');
    }
    return data;
  }

  function tierOf(item, config) {
    const id = String(item.variant_id);
    if (config.tier2.includes(id)) return 2;
    if (config.tier1.includes(id)) return 1;
    if (item.properties && item.properties._free_gift) return -1; // gift no longer configured
    return 0;
  }

  function analyse(cart, config) {
    const gifts = [];
    let paid = 0;
    for (const item of cart.items) {
      if (!(Number(item.quantity) > 0)) continue;
      const tier = tierOf(item, config);
      if (tier === 0) paid += Number(item.original_line_price) || 0;
      else gifts.push({ item, tier });
    }
    const eligible = paid >= config.t2 ? 2 : paid > 0 && paid >= config.t1 ? 1 : 0;
    return { gifts, paid, eligible };
  }

  /**
   * Returns a {lineKey: quantity} map of fixes, or null when the cart is already valid.
   * ONE free gift per order, quantity 1. Gifts from a list the cart no longer unlocks
   * (or no longer configured) are removed; if several gifts are present, the highest unlocked one stays.
   */
  function planFixes(cart, config) {
    const { gifts, eligible } = analyse(cart, config);
    if (!gifts.length) return null;

    const keep =
      gifts.filter(({ tier }) => tier > 0 && tier <= eligible).sort((a, b) => b.tier - a.tier)[0] || null;
    const updates = {};

    for (const { item } of gifts) {
      if (keep && item.key === keep.item.key) {
        if (item.quantity !== 1) updates[item.key] = 1;
      } else {
        updates[item.key] = 0;
      }
    }
    return Object.keys(updates).length ? updates : null;
  }

  /** Serialises our own cart work so two operations never interleave. */
  function exclusive(task) {
    pendingWork += 1;
    running = running
      .catch(() => {})
      .then(async () => {
        try {
          return await task();
        } finally {
          pendingWork -= 1;
        }
      });
    return running;
  }

  /* ---------- tell the theme the cart changed ---------- */

  const isCartPage = () => !!document.querySelector('[data-cart-page]');

  /** The cart page is server-rendered, so it reloads; a guard stops a reload loop. */
  function reloadCartPage() {
    try {
      const key = 'ob-free-gift-reloads';
      const now = Date.now();
      const log = JSON.parse(sessionStorage.getItem(key) || '[]').filter((t) => now - t < 20000);
      if (log.length >= 3) return;
      log.push(now);
      sessionStorage.setItem(key, JSON.stringify(log));
    } catch (error) {
      /* storage unavailable: reload anyway */
    }
    location.reload();
  }

  function announce(cart) {
    if (isCartPage()) {
      reloadCartPage();
      return;
    }
    const ui = window.OblivionUI;
    if (!ui || typeof ui.renderCart !== 'function') return;
    announcing = true; // renderCart fires `botanical:cart` synchronously; don't re-check our own update
    try {
      ui.renderCart(cart);
    } finally {
      announcing = false;
    }
  }

  /* ---------- reconcile ---------- */

  function reconcile() {
    return exclusive(async () => {
      const config = readConfig();
      if (!config || !config.enabled) return false;
      const cart = await getCart();
      const updates = planFixes(cart, config);
      if (!updates) return false;
      const result = await post('cart/update.js', { updates });
      announce(result);
      return true;
    });
  }

  function scheduleReconcile(delay = 350) {
    clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(() => {
      reconcileTimer = null;
      reconcile().catch((error) => console.warn('[ob-free-gift] reconcile failed', error));
    }, delay);
  }

  /* ---------- select ---------- */

  function setStatus(button, message) {
    const status = button.closest('[data-ob-free-gift]')?.querySelector('[data-ob-free-gift-status]');
    if (status) status.textContent = message || '';
  }

  function selectGift(button) {
    const variantId = String(button.dataset.variantId || '');
    const tier = Number(button.dataset.tier);
    if (!variantId || !tier) return;

    button.setAttribute('aria-busy', 'true');
    setStatus(button, '');

    return exclusive(async () => {
      const config = readConfig();
      if (!config) throw new Error('Chocolate is not available right now.');
      let cart = await getCart();
      const { gifts, eligible } = analyse(cart, config);

      if (tier > eligible) throw new Error('Your cart no longer qualifies for this gift.');
      const tierList = tier === 2 ? config.tier2 : config.tier1;
      if (!tierList.includes(variantId)) throw new Error('This gift is not part of the offer.');

      const already = gifts.find(({ item }) => String(item.variant_id) === variantId);
      if (already && gifts.length === 1 && already.item.quantity === 1) return;

      // Add the new gift first so a failed add never leaves the customer without their current gift.
      if (!already) {
        await post('cart/add.js', {
          items: [{ id: Number(variantId), quantity: 1, properties: { _free_gift: String(tier) } }],
        });
      }

      cart = await getCart();
      const updates = {};
      let kept = false;
      for (const item of cart.items) {
        if (tierOf(item, config) === 0) continue;
        if (String(item.variant_id) === variantId && !kept) {
          kept = true;
          if (item.quantity !== 1) updates[item.key] = 1;
        } else {
          updates[item.key] = 0; // one gift per order: every other gift line goes
        }
      }

      const result = Object.keys(updates).length ? await post('cart/update.js', { updates }) : cart;
      announce(result);
    })
      .catch((error) => {
        setStatus(button, error.message || 'Could not add the gift. Please try again.');
      })
      .finally(() => {
        if (button.isConnected) button.removeAttribute('aria-busy');
      });
  }

  /* ---------- checkout guard ---------- */

  /*
   * Checkout clicks normally pass straight through (GoKwik needs the original user gesture to open).
   * Only when a gift correction is pending or in flight do we hold the click, finish the correction,
   * and ask the customer to tap Checkout again — so an ineligible gift can never be carried into checkout.
   */
  const CHECKOUT_SELECTOR =
    '[data-botanical-checkout], .gokwik-checkout button, button[name="checkout"], #checkout, a[href*="/checkout"]';

  function correctionPending() {
    if (pendingWork > 0 || reconcileTimer !== null) return true;
    return !!document.querySelector('[data-ob-free-gift][data-needs-fix="true"]');
  }

  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target instanceof Element ? event.target.closest(CHECKOUT_SELECTOR) : null;
      if (!trigger || !correctionPending()) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const status = document.querySelector('[data-ob-free-gift-status]');
      if (status) status.textContent = 'Updating your ₹1 chocolate… please tap Checkout again.';
      clearTimeout(reconcileTimer);
      reconcileTimer = null;
      reconcile().catch((error) => console.warn('[ob-free-gift] pre-checkout check failed', error));
    },
    true
  );

  /* ---------- wiring ---------- */

  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-ob-free-gift-select]') : null;
    if (!button || button.disabled || button.getAttribute('aria-busy') === 'true') return;
    event.preventDefault();
    selectGift(button);
  });

  // Every drawer cart change (qty, remove, add, quick add) is re-checked; our own updates are skipped.
  document.addEventListener('botanical:cart', () => {
    if (announcing) return;
    scheduleReconcile();
  });

  function initialCheck() {
    readConfig();
    const flagged = document.querySelector('[data-ob-free-gift][data-needs-fix="true"]');
    scheduleReconcile(0);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialCheck);
  else initialCheck();

  window.OBFreeGift = { reconcile, readConfig, planFixes, analyse };
})();
